import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Collapse from '@mui/material/Collapse'
import Typography from '@mui/material/Typography'
import type { SxProps, Theme } from '@mui/material/styles'

// The one house style for a settings block: an outlined Card with an `h6` title and an optional
// secondary description. `collapsible` adds a clickable chevron header wrapping the body in a
// Collapse — used where a block sits above other content (e.g. above a sync table) and should be
// tucked away by default; plain config cards leave it off. When collapsible, `open`/`onToggle`
// make it controllable so the parent can gate work on the open state (e.g. lazy-load on expand).
export default function SettingsSection({
    title, description, collapsible = false, defaultOpen = true, open: openProp, onToggle, sx, children,
}: {
    title: React.ReactNode
    description?: React.ReactNode
    collapsible?: boolean
    defaultOpen?: boolean
    open?: boolean
    onToggle?: (open: boolean) => void
    sx?: SxProps<Theme>
    children: React.ReactNode
}) {
    const [openState, setOpenState] = useState(defaultOpen)
    const isControlled = openProp !== undefined
    const open = collapsible ? (isControlled ? openProp : openState) : true
    const toggle = () => {
        const next = !open
        if (!isControlled) setOpenState(next)
        onToggle?.(next)
    }

    const body = (
        <>
            {description !== undefined &&
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{description}</Typography>}
            {children}
        </>
    )

    return (
        <Card variant="outlined" sx={{ p: 2, mb: 2, ...sx }}>
            {collapsible
                ? <Box onClick={toggle}
                       sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none' }}>
                    <Typography component="span" sx={{ width: '1rem', color: 'text.secondary' }}>{open ? '▾' : '▸'}</Typography>
                    <Typography variant="h6">{title}</Typography>
                  </Box>
                : <Typography variant="h6">{title}</Typography>}
            {collapsible
                ? <Collapse in={open}><Box sx={{ pt: 1 }}>{body}</Box></Collapse>
                : body}
        </Card>
    )
}
